import React from 'react';
import { formatCalendarDate } from '../utils/dateDisplay';
import './ReceivingInvoiceDocument.css';

const wrap = (value, limit) => {
  const words = String(value || '').trim().split(/\s+/).filter(Boolean);
  const lines = [];
  for (const word of words) {
    if (lines.length && (lines[lines.length - 1] + ' ' + word).length <= limit) lines[lines.length - 1] += ' ' + word;
    else lines.push(word);
  }
  return lines;
};
const Text = ({ x, y, size = 8.5, bold = false, align = 'start', children, serif = false }) => (
  <text x={x} y={792 - y} fontSize={size} fontWeight={bold ? 700 : 400}
    textAnchor={align} fontFamily={serif ? 'Times New Roman, serif' : 'Arial, sans-serif'}>{children}</text>
);
const Box = ({ x, y, width, height, fill = 'white' }) => (
  <rect x={x} y={792 - y - height} width={width} height={height} fill={fill} stroke="#4d4d4d" strokeWidth="0.8" />
);
const Line = ({ x = 34, y, end = 578, thick = false }) => (
  <line x1={x} y1={792 - y} x2={end} y2={792 - y} stroke="#4d4d4d" strokeWidth={thick ? 1.2 : 0.8} />
);
const Address = ({ address = {}, title, x }) => {
  const seenText = new Set();
  const seenPhones = new Set();
  const lines = [address.name, address.addr1, address.addr2, address.addr3, address.addr4,
    [address.city, address.state, address.zip, address.country].filter(Boolean).join(' '),
    address.phone, address.website, address.email].filter(value => {
      const text = String(value || '').trim();
      if (!text) return false;
      const key = text.toLowerCase().replace(/\s+/g, ' ');
      const digits = text.replace(/[^0-9]/g, '');
      if (seenText.has(key) || (digits.length >= 7 && seenPhones.has(digits))) return false;
      seenText.add(key);
      if (digits.length >= 7) seenPhones.add(digits);
      return true;
    }).slice(0, 7);
  return <>
    <Box x={x} y={550} width={235} height={100} />
    <Box x={x} y={632} width={235} height={18} fill="#f2f2f2" />
    <Text x={x + 6} y={638}>{title}:</Text>
    {lines.map((line, index) => <Text key={index} x={x + 7} y={618 - index * 10} bold={index === 0}>{line}</Text>)}
  </>;
};

// Use the same Letter-page coordinates as the receiving email PDF.
export default function ReceivingInvoiceDocument({ invoice }) {
  const items = invoice.items || [];
  const pages = [];
  for (let i = 0; i < items.length; i += 13) pages.push(items.slice(i, i + 13));
  if (!pages.length) pages.push([]);
  const total = items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  return <div className="receiving-document">
    {pages.map((pageItems, pageIndex) => {
      let rowY = 498;
      return <svg key={pageIndex} className="receiving-document-page" xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 612 792" role="img" aria-label={'Receiving invoice ' + invoice.invoiceNo + ', page ' + (pageIndex + 1)}>
        <rect width="612" height="792" fill="white" />
        <Text x={34} y={742} size={17} bold>RECEIVING INVOICE</Text>
        <Text x={300} y={724} size={21} align="middle" serif>IRENE’S</Text>
        <Text x={300} y={708} size={13} bold align="middle">nyc</Text>
        {[['Invoice #', 'Date'], [invoice.invoiceNo, formatCalendarDate(invoice.date)], ['Ship Method', invoice.shipMethod || '']].map((row, index) =>
          row.map((value, col) => <g key={index + '-' + col}>
            <Box x={410 + col * 84} y={715 - index * 24} width={84} height={24} fill={index === 0 ? '#f0f0f0' : 'white'} />
            <Text x={452 + col * 84} y={724 - index * 24} align="middle">{value}</Text>
          </g>))}
        <Address x={34} title="From" address={invoice.from} />
        <Address x={343} title="To" address={invoice.to} />
        {[['Serial #', 34, 163], ['Description', 197, 305], ['Quantity', 502, 76]].map(([title, x, width]) => <g key={title}>
          <Box x={x} y={518} width={width} height={24} fill="#f0f0f0" />
          <Text x={x + width / 2} y={527} size={9} align="middle">{title}</Text>
        </g>)}
        {pageItems.map((item, index) => {
          const serials = wrap(item.serial, 30).slice(0, 2);
          const descriptions = wrap(item.description, 42).slice(0, 2);
          const y = rowY;
          rowY -= Math.max(20, Math.max(serials.length, descriptions.length, 1) * 10 + 4);
          return <g key={index}>
            {serials.map((line, i) => <Text key={i} x={39} y={y - i * 10}>{line}</Text>)}
            {descriptions.map((line, i) => <Text key={i} x={202} y={y - i * 10}>{line}</Text>)}
            <Text x={573} y={y} align="end">{item.quantity}</Text>
          </g>;
        })}
        {pageIndex === pages.length - 1 && <>
          <Line y={158} thick />
          <Text x={497} y={144} size={9} align="end">Sub-Total</Text>
          <Text x={573} y={144} size={9} align="end">{total}</Text>
          <Line x={502} y={133} />
          <Text x={497} y={118} size={9} bold align="end">Total</Text>
          <Text x={573} y={118} size={9} bold align="end">{total}</Text>
          <Line y={107} thick />
          <Text x={573} y={90} bold align="end">Prepared By: {invoice.preparedBy || '-'}</Text>
          <Box x={34} y={25} width={544} height={48} />
          <Text x={41} y={59} bold>Comment:</Text>
          {wrap(invoice.comment || invoice.memo || '-', 95).slice(0, 3).map((line, index) => <Text key={index} x={87} y={59 - index * 10}>{line}</Text>)}
        </>}
        {pages.length > 1 && <Text x={578} y={18} size={6.5} align="end">Page {pageIndex + 1} of {pages.length}</Text>}
      </svg>;
    })}
  </div>;
}
